// src/components/Stream/SongList.jsx

import React, { useState, useEffect } from "react";
import {
  Box,
  Dialog,
  Divider,
  IconButton,
  Slide,
  Stack,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import LockIcon from "@mui/icons-material/Lock";

import ApiService from "../../services/api-service";
import { start } from "../../action/streamSong";
import Loader from "../Loader";
import NoData from "../Nodata";
import notifier from "../Notifier";

import { Images } from "../../assets/common";
import {
  HMS,
  getStreamingBalance,
  isOwnerId,
  truncate,
} from "../../constants/function";

// The Redux-based StreamPlayer (which should, on end of a track, dispatch next)
import StreamPlayer from "./streamPlayer";

/**
 * Slide-up transition for Dialog
 */
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

/**
 * A single row to display and load a song.
 */
function SongRow({ song, index, playing, track, onLoader, onPlaySong, requestInfo }) {
  const [progress, setProgress] = useState(0);
  const [isHovered, setIsHovered] = useState(false); // State to track hover
  
  // Check if this song has been requested
  const hasRequest = !!requestInfo;
  const tipAmount = hasRequest ? requestInfo.tipAmount : null;
  
  // Debug logging
  if (song.title === "Trunk" || index === 0) {
    console.log(`[SongRow] Debug - Song: ${song.title}, ID: ${song._id}`);
    console.log(`[SongRow] Debug - RequestInfo:`, requestInfo);
    console.log(`[SongRow] Debug - HasRequest: ${hasRequest}, TipAmount: ${tipAmount}`);
  }


  // Download the file with progress
  async function fetchSong() {
    console.log(`[SongRow] => user clicked index=${index}, ID=${song?._id}`);

    // 1) Check streaming balance
    const balance = getStreamingBalance();
    console.log(`[SongRow] => balance=${balance}`);
    if (!balance || balance <= 0) {
      notifier.warning("Not enough streaming balance.");
      return;
    }

    // 2) If already flagged as playing => skip
    if (song.isPlaying) {
      console.log("[SongRow] => This song is flagged as playing => skip");
      return;
    }

    // 3) If there's already a track loaded, we might just resume it
    if (track) {
      console.log("[SongRow] => track exists => onPlaySong(track, true) to resume");
      onPlaySong(track, true);
      return;
    }

    // 4) If user not subscribed and song is private => block
    if (!isOwnerId(song?.uploader?._id) && song.private && !song?.uploader?.isSubscribed) {
      console.log("[SongRow] => private + unsubscribed => block");
      notifier.error("Song is private; you are not subscribed.");
      return;
    }

    onLoader(true);
    console.log(`[SongRow] => begin fetch => ${song.fileId?.filePath || "N/A"}`);

    // 5) Start streaming the song
    try {
      // Create a media source URL directly from the file path
      const audioUrl = song.fileId?.filePath;
      console.log("[SongRow] => streaming from URL:", audioUrl);
      
      // Pass the URL instead of blob for streaming
      onPlaySong(audioUrl, song);
      onLoader(false);
    } catch (err) {
      console.error("[SongRow] => fetchSong => error:", err);
      onLoader(false);
    }
  }

  // Helper to show a progress log in the console
  async function fetchWithProgress(url) {
    const res = await fetch(url);
    const total = +res.headers.get("Content-Length") || 0;
    let loaded = 0;

    const reader = res.body.getReader();
    const stream = new ReadableStream({
      start(controller) {
        function pump() {
          return reader.read().then(({ done, value }) => {
            if (done) {
              controller.close();
              return;
            }
            controller.enqueue(value);
            loaded += value.length;

            if (total) {
              const pct = ((loaded / total) * 100).toFixed(2);
              setProgress(pct);
              console.log(`[SongRow] => Download => ${pct}%`);
            } else {
              console.log(`[SongRow] => chunk => size=${value.length}`);
            }
            return pump();
          });
        }
        return pump();
      },
    });

    return new Response(stream, res);
  }

  return (
    <Stack direction="column" sx={{ paddingTop: "5px" }}>
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      spacing={2}
      sx={{ 
        padding: "0 16px",
        ...(hasRequest && {
          border: "3px solid #ffc107",
          borderRadius: "8px",
          backgroundColor: "rgba(255, 193, 7, 0.1)",
          margin: "4px 8px",
          padding: "8px 16px",
        })
      }}
    >
        {/* Left side: thumbnail + title */}
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box
            className="song-container"
            sx={{ position: "relative" }}
            onMouseEnter={() => setIsHovered(true)} // Show play button on hover
            onMouseLeave={() => setIsHovered(false)} // Hide play button when not hovering
          >
            <img
              className="avatar"
              src={song?.thumbnailImg?.filePath || Images.default_music}
              alt="songThumb"
            />
            {isHovered && ( // Conditionally render the play button
              <img
                className="__play_now"
                src={Images.play}
                alt="play"
                onClick={fetchSong}
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  zIndex: 10,
                  cursor: "pointer",
                }}
              />
            )}
          </Box>
        <Stack>
           {/* Add onClick to the song title */}
           <Typography
              sx={{ fontSize: 17, cursor: "pointer" }}
              onClick={fetchSong} // Allow clicking the song name
            >
              {truncate(song.title, 30)}
            </Typography>
            {song.private && (
              <LockIcon
                sx={{ fontSize: 14, color: "#fff", background: "#4E4E4E" }}
              />
            )}
          </Stack>
      </Stack>
  
      {/* Right side: Tip amount, Now-playing indicator + duration */}
      <Stack direction="row" alignItems="center" spacing={1}>
        {tipAmount && (
          <Box
            sx={{
              backgroundColor: "#ffc107",
              color: "#000",
              padding: "4px 8px",
              borderRadius: "12px",
              fontWeight: "bold",
              fontSize: "12px",
              display: "flex",
              alignItems: "center",
              gap: "4px",
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            {tipAmount} SIKI
          </Box>
        )}
        {playing === index && (
          <img style={{ width: 14 }} src={Images.listenYellow} alt="playing" />
        )}
        <Typography sx={{ fontSize: 13 }}>{HMS(song.duration)}</Typography>
      </Stack>
      </Stack>
    </Stack>
  );
}

/**
 * The aggregator SongList:
 * - fetches a playlist from the server
 * - user can pick a song, or click next/prev in StreamPlayer
 * - if track===null => we auto fetch/publish the next track
 */
export default function SongList({
  open,
  handleClose,
  roomId,
  liveUsers,
  publishMusicTrack,
  stopMusicTrack,
}) {
  const dispatch = useDispatch();
  const { songs, index, track, vol, pos } = useSelector((state) => state.streamSong);

  const [loader, setLoader] = useState(false);
  const [acceptedRequests, setAcceptedRequests] = useState([]);

  // Turn off loader if a track is marked "isPlaying"
  useEffect(() => {
    if (songs[index]?.isPlaying) {
      setLoader(false);
    }
  }, [index, songs]);

  // On open, fetch playlist if we don't have songs yet OR if roomId changed
  const [lastRoomId, setLastRoomId] = useState(null);
  
  useEffect(() => {
    console.log(`[SongList] => open=${open}, roomId=${roomId}, lastRoomId=${lastRoomId}`);
    console.log(`[SongList] => current songs length=${songs.length}, index=${index}`);
    if (!open || !roomId) {
      console.log("[SongList] => Returning early - open or roomId missing");
      return;
    }

    // Always fetch playlist when opening with a roomId
    // This ensures we get fresh data whether coming from Go Live or My Music
    console.log("[SongList] => Fetching playlist for roomId:", roomId);
    getPlaylist(roomId);
    fetchAcceptedRequests(roomId);
    setLastRoomId(roomId);
  }, [open, roomId]);

  // Fetch accepted song requests
  async function fetchAcceptedRequests(roomId) {
    try {
      console.log("[SongList] => Fetching accepted song requests for roomId:", roomId);
      const response = await ApiService.SONG_REQUEST_ACCEPTED_LIST(roomId);
      console.log("[SongList] => API Response:", response);
      if (response.success) {
        console.log("[SongList] => Accepted requests:", response.result);
        console.log("[SongList] => Number of accepted requests:", response.result?.length || 0);
        // Log first request if any
        if (response.result && response.result.length > 0) {
          console.log("[SongList] => First accepted request:", response.result[0]);
        }
        setAcceptedRequests(response.result || []);
      } else {
        console.log("[SongList] => API returned success: false");
      }
    } catch (error) {
      console.error("[SongList] => Error fetching accepted requests:", error);
      console.error("[SongList] => Error details:", error.response || error.message);
    }
  }

  /**
   * This effect is the "auto next" logic:
   * If Redux says (index>=0 && track===null), we fetch & publish the next track automatically.
   */
  useEffect(() => {
    if (index < 0) {
      console.log("[SongList] => index < 0 => not fetching anything yet.");
      return;
    }
    if (track) {
      console.log("[SongList] => track!=null => we already have a playing track => do nothing");
      return;
    }
    console.log(
      `[SongList] => aggregator => index=${index}, track=null => auto fetch/publish nextSong`
    );
    autoFetchAndPublish(index);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, track]);

  // This actually fetches the next track from the server and publishes it
  async function autoFetchAndPublish(newIndex) {
    if (newIndex < 0 || newIndex >= songs.length) {
      console.warn("[SongList] => invalid newIndex => skip auto");
      return;
    }
    // Stop the old track (if any)
    if (stopMusicTrack) {
      console.log("[SongList] => auto => calling stopMusicTrack() on old track");
      await stopMusicTrack();
    }

    const song = songs[newIndex];
    if (!song?.fileId?.filePath) {
      console.error("[SongList] => No filePath => cannot fetch this song");
      return;
    }

    try {
      setLoader(true);
      const audioUrl = song.fileId.filePath;
      console.log(`[SongList] => auto => streaming from URL => ${audioUrl}`);

      // Check if this song has an accepted request
      const requestInfo = acceptedRequests.find(req => 
        req.songId?.toString() === song._id?.toString() || 
        req.songId === song._id
      );

      let newTrack = null;
      if (publishMusicTrack) {
        console.log("[SongList] => auto => publishMusicTrack() => volume =", vol || 80);
        // Use URL streaming for auto-play too
        newTrack = await publishMusicTrack(audioUrl, vol || 80, true);
      }

      // If this song had a request, mark it as played
      if (requestInfo && requestInfo._id) {
        console.log("[SongList] => auto => Marking song request as played:", requestInfo._id);
        try {
          await ApiService.SONG_REQUEST_MARK_PLAYED(requestInfo._id);
          // Remove the request from our local state
          setAcceptedRequests(acceptedRequests.filter(req => req._id !== requestInfo._id));
        } catch (error) {
          console.error("[SongList] => auto => Error marking song as played:", error);
        }
      }

      // Once we have the new track, store it in Redux => the player will handle playback
      console.log(`[SongList] => auto => dispatch => index=${newIndex}, got new track`);
      dispatch(
        start({
          index: newIndex,
          songs,
          pos: 0,
          track: newTrack,
          vol: vol || 80,
        })
      );
    } catch (err) {
      console.error("[SongList] => autoFetchAndPublish => error =>", err);
    } finally {
      setLoader(false);
    }
  }

  /**
   * Manually fetch the playlist from the server
   * Increase pageSize so we get enough songs to test "next" logic
   */
  async function getPlaylist(playlistId) {
    try {
      setLoader(true);
      console.log("[SongList] => getPlaylist => trying both APIs");
      console.log("[SongList] => playlistId:", playlistId);
      
      // First try PLAYLIST_DETAIL to get the full playlist data
      try {
        console.log("[SongList] => Trying PLAYLIST_DETAIL API first");
        const detailRes = await ApiService.PLAYLIST_DETAIL({ _id: playlistId });
        console.log("[SongList] => PLAYLIST_DETAIL response:", detailRes);
        
        if (detailRes?.result?.music && detailRes.result.music.length > 0) {
          const musicArr = detailRes.result.music;
          console.log(`[SongList] => PLAYLIST_DETAIL returned ${musicArr.length} songs`);
          
          // Preserve current playing state when updating song list
          const currentState = {
            index: index >= 0 ? index : -1,
            pos: index >= 0 ? pos : 0,  // Preserve current progress
            track: track || null,  // Keep current track if playing
            vol: vol || 10,
          };
          
          console.log(`[SongList] => Preserving current state:`, currentState);
          
          dispatch(
            start({
              index: currentState.index,
              songs: musicArr,
              pos: currentState.pos,
              track: currentState.track,
              vol: currentState.vol,
            })
          );
          return;
        }
      } catch (detailErr) {
        console.log("[SongList] => PLAYLIST_DETAIL failed:", detailErr);
      }
      
      // Fall back to GET_PLAYLIST_MUSIC
      console.log("[SongList] => Trying GET_PLAYLIST_MUSIC API");
      const queryParams = `_id=${playlistId}&pageNo=1&pageSize=100`;
      const res = await ApiService.GET_PLAYLIST_MUSIC(queryParams);

      console.log("[SongList] => GET_PLAYLIST_MUSIC response:", res);
      const musicArr = res?.result || [];

      if (musicArr.length) {
        console.log(`[SongList] => GET_PLAYLIST_MUSIC returned ${musicArr.length} songs`);
        
        // Preserve current playing state when updating song list
        const currentState = {
          index: index >= 0 ? index : -1,
          pos: index >= 0 ? pos : 0,  // Preserve current progress
          track: track || null,  // Keep current track if playing
          vol: vol || 10,
        };
        
        console.log(`[SongList] => Preserving current state (fallback):`, currentState);
        
        dispatch(
          start({
            index: currentState.index,
            songs: musicArr,
            pos: currentState.pos,
            track: currentState.track,
            vol: currentState.vol,
          })
        );
      } else {
        console.warn("[SongList] => No songs returned from either API!");
        console.log("[SongList] => playlistId was:", playlistId);
      }
    } catch (err) {
      console.error("[SongList] => getPlaylist => error =>", err);
    } finally {
      setLoader(false);
    }
  }

  /**
   * Manually triggered by a user click on a SongRow:
   *  - fetch the track
   *  - publish track
   *  - store in Redux
   */
  async function handlePlaySong(audioSource, songData) {
    // If songData is a number, it's the index from auto-play
    const newIndex = typeof songData === 'number' ? songData : songs.findIndex(s => s._id === songData._id);
    console.log(`[SongList] => handlePlaySong => newIndex=${newIndex}`);
    
    try {
      setLoader(true);

      // Check if this song has an accepted request
      const song = songs[newIndex];
      const requestInfo = acceptedRequests.find(req => 
        req.songId?.toString() === song._id?.toString() || 
        req.songId === song._id
      );

      // Stop any existing track first
      if (stopMusicTrack) {
        console.log("[SongList] => handlePlaySong => calling stopMusicTrack()");
        await stopMusicTrack();
      }

      let realTrack = null;
      
      // Handle both URL streaming and blob playback
      if (publishMusicTrack) {
        if (typeof audioSource === 'string') {
          // URL streaming - create minimal audio element for quick start
          console.log("[SongList] => streaming from URL => volume=", vol || 80);
          realTrack = await publishMusicTrack(audioSource, vol || 80, true); // true = streaming mode
        } else if (audioSource instanceof Blob) {
          // Blob playback (for auto-play)
          console.log("[SongList] => publishMusicTrack => volume=", vol || 80);
          realTrack = await publishMusicTrack(audioSource, vol || 80);
        }
      }

      // If this song had a request, mark it as played
      if (requestInfo && requestInfo._id) {
        console.log("[SongList] => Marking song request as played:", requestInfo._id);
        try {
          await ApiService.SONG_REQUEST_MARK_PLAYED(requestInfo._id);
          // Remove the request from our local state
          setAcceptedRequests(acceptedRequests.filter(req => req._id !== requestInfo._id));
        } catch (error) {
          console.error("[SongList] => Error marking song as played:", error);
        }
      }

      // Store the new track in Redux
      console.log(`[SongList] => userPick => dispatch => index=${newIndex}`);
      dispatch(
        start({
          index: newIndex,
          songs,
          pos: 0,
          track: realTrack,
          vol: vol || 80,
        })
      );
    } catch (err) {
      console.error("[SongList] => handlePlaySong => error =>", err);
    } finally {
      setLoader(false);
    }
  }

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      keepMounted
      onClose={handleClose}
      PaperProps={{
        style: {
          background: "#1A1A1A",
          boxShadow: "none",
          color: "#ffffff",
          borderRadius: 16,
          width: "444px",
          top: "28px",
        },
      }}
    >
      {/* Header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ padding: "10px 16px 0", height: 46 }}
      >
        <Typography sx={{ fontSize: 16 }}>Songs</Typography>
        <Stack direction="row" alignItems="center">
          <IconButton onClick={() => getPlaylist(roomId)} sx={{ mr: 2 }}>
            <RefreshIcon />
          </IconButton>
          <IconButton onClick={() => handleClose(false)}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </Stack>

      <Divider sx={{ bgcolor: "#1E1E1E", height: 2 }} />

      {/* Song list (scrollable) */}
      <Stack
        sx={{
          position: "relative",
          padding: "16px 0 70px 0",
          height: "calc(100vh - 208px)",
          overflow: "auto",
        }}
      >
        {songs.length ? (
          songs.map((song, i) => {
            // Find if this song has an accepted request
            // Compare both as strings to handle ObjectId comparisons
            const requestInfo = acceptedRequests.find(req => 
              req.songId?.toString() === song._id?.toString() || 
              req.songId === song._id
            );
            return (
              <SongRow
                key={song._id || i}
                song={song}
                index={i}
                playing={index}
                track={i === index ? track : null}
                onLoader={setLoader}
                onPlaySong={(audioSource, songData) => handlePlaySong(audioSource, songData || i)}
                requestInfo={requestInfo}
              />
            );
          })
        ) : (
          <NoData lable="No songs" />
        )}
      </Stack>

      {/* The Redux-based StreamPlayer at the bottom */}
      <StreamPlayer roomId={roomId} />

      {/* Loader overlay */}
      {loader && <Loader wait _class="__loader_inside" />}
    </Dialog>
  );
}
